import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { StoreDto } from '@fewerbags/schema';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { saveLastStoreId } from '../../lib/lastStore';
import { groceryKeys } from '../../pages/groceries/queries';
import { Alert } from '../Alert';
import { Button } from '../Button';
import { Dialog } from '../Dialog';
import { StorePicker } from './StorePicker';

/** Change a list's store — re-sorts every item the next time it's fetched. */
export function ChangeStoreDialog({
  open,
  onClose,
  listId,
  currentStoreId,
}: {
  open: boolean;
  onClose: () => void;
  listId: string;
  currentStoreId: string | null;
}) {
  const qc = useQueryClient();
  const [store, setStore] = useState<StoreDto | null>(null);
  const [picked, setPicked] = useState(false);

  const update = useMutation({
    mutationFn: () => api.groceries.update(listId, { storeId: store?.id ?? null }),
    onSuccess: async (list) => {
      saveLastStoreId(list.storeId);
      await Promise.all([
        qc.invalidateQueries({ queryKey: groceryKeys.detail(listId) }),
        qc.invalidateQueries({ queryKey: groceryKeys.all }),
      ]);
      onClose();
    },
  });

  return (
    <Dialog open={open} onClose={onClose} title="Change store">
      <div className="flex flex-col gap-4">
        <StorePicker
          selectedId={picked ? (store?.id ?? null) : currentStoreId}
          onPick={(s) => {
            setStore(s);
            setPicked(true);
          }}
        />
        {update.isError ? <Alert tone="error">{messageForError(update.error)}</Alert> : null}
        <div className="flex gap-2">
          <Button onClick={() => update.mutate()} disabled={update.isPending || !picked}>
            {update.isPending ? 'Saving…' : 'Use this store'}
          </Button>
          <Button variant="secondary" onClick={onClose} disabled={update.isPending}>
            Cancel
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
